import ColorPicker from '@/components/commons/color-picker';
import ImageSetting from '@/components/sections/builder/settings/image-setting';
import InputTab from '@/components/sections/builder/settings/input-tab';
import React from 'react';
import { ColorResult } from 'react-color';

export type BackgroundStyleType = {
  type: 'image' | 'color';
  backgroundImage?: string;
  backgroundColor?: string;
  backgroundOpacity?: string;
  backgroundLayout?: string;
  backgroundPosition: string;
  backgroundAttachment: string;
  backgroundSize: string;
};

type BackgroundSettingType = {
  label?: string;
  value?: BackgroundStyleType;
  onChange?: (background?: BackgroundStyleType) => void;
  isShowBackgroundLayout?: boolean;
};

export const buildBackgroundWithOpacity = (color: string, opacity?: number) => {
  const a = (Number(opacity || 100) / 100).toFixed(1);

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
  if (result) {
    const rgb = {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    };

    return `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`;
  }

  return color;
};

export default function BackgroundSetting({
  label = 'Background',
  value,
  onChange,
  isShowBackgroundLayout = true,
}: BackgroundSettingType) {
  const background: BackgroundStyleType = value || {
    type: 'color',
    backgroundPosition: 'center',
    backgroundAttachment: 'scroll',
    backgroundSize: 'cover',
  };

  const onChangeBackground = (value: { [key: string]: unknown }) => {
    onChange?.({
      ...background,
      ...value,
    });
  };

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      onChangeBackground({
        backgroundColor: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      onChangeBackground({ backgroundColor: color.hex });
    }
  };

  return (
    <>
      <h2 className=''>{label}</h2>
      <div className='py-3'>
        <InputTab
          grid={2}
          defaultValue={background.type}
          options={[
            { label: 'Color', value: 'color' },
            { label: 'Image', value: 'image' },
          ]}
          onChange={(v) => onChangeBackground({ type: v })}
        />
      </div>
      {background.type === 'color' && (
        <>
          <ColorPicker
            color={background.backgroundColor}
            inputColor={background.backgroundColor}
            percentage={background.backgroundOpacity}
            onChangePercentage={(val) => onChangeBackground({ backgroundOpacity: val })}
            onChangeColorComplete={(color) => onChangeColor(color)}
            onChangeInputColor={(e) => onChangeBackground({ backgroundColor: e.target.value })}
          />
          <h2 className='mb-2 mt-4 text-xs'>Background Layout</h2>
          <InputTab
            defaultValue={background.backgroundLayout || 'full_width'}
            grid={2}
            options={[
              { label: 'Full width', value: 'full_width' },
              { label: 'Grid', value: 'grid' },
            ]}
            onChange={(v) => onChangeBackground({ backgroundLayout: v })}
          />
        </>
      )}
      {background.type === 'image' && (
        <>
          <ImageSetting
            value={background?.backgroundImage}
            onChange={(v) => onChangeBackground({ backgroundImage: v })}
          />
          {isShowBackgroundLayout && (
            <>
              <h2 className='mb-2 mt-4 text-xs'>Background Layout</h2>
              <InputTab
                defaultValue={background.backgroundLayout || 'full_width'}
                grid={2}
                options={[
                  { label: 'Full width', value: 'full_width' },
                  { label: 'Grid', value: 'grid' },
                ]}
                onChange={(v) => onChangeBackground({ backgroundLayout: v })}
              />
            </>
          )}

          <h2 className='mb-2 mt-4 text-xs'>Background Attachment</h2>
          <InputTab
            grid={2}
            defaultValue={background.backgroundAttachment}
            options={[
              { label: 'Scroll', value: 'scroll' },
              { label: 'Fixed', value: 'fixed' },
            ]}
            onChange={(v) => onChangeBackground({ backgroundAttachment: v })}
          />
        </>
      )}
    </>
  );
}
